import { useState, useCallback, useEffect, useRef } from 'react';
import { adminApi } from '../../api/adminApi';
import { FK_MAP, ARRAY_FK_MAP, CONTAINERS, CONTAINER_DISPLAY_FIELDS, type FkTarget } from './explorerSchema';
import { ExplorerTree, createRootNode, type TreeNode } from './ExplorerTree';
import './ExplorerTab.css';

// ─── FK Hover Preview Cache & Helper ─────────────────────────────────────────
const fkPreviewCache = new Map<string, Record<string, unknown> | null>();

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((o, key) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[key] : undefined), obj);
}

function FkHoverPreview({ container, id, fk }: { container: string; id: string; fk: FkTarget }) {
  const [preview, setPreview] = useState<Record<string, unknown> | null | undefined>(undefined);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const cacheKey = `${container}:${id}`;
    const cached = fkPreviewCache.get(cacheKey);
    if (cached !== undefined) { setPreview(cached); return; }

    adminApi.getExplorerDocument(container, id).then(res => {
      if (!mountedRef.current) return;
      fkPreviewCache.set(cacheKey, res.document);
      setPreview(res.document);
    }).catch(() => {
      if (mountedRef.current) { fkPreviewCache.set(cacheKey, null); setPreview(null); }
    });
    return () => { mountedRef.current = false; };
  }, [container, id]);

  const fields = fk.displayFields || CONTAINER_DISPLAY_FIELDS[container] || [];
  if (preview === undefined) return <div className="fk-preview-popup">Loading...</div>;
  if (!preview) return <div className="fk-preview-popup">Not found</div>;

  return (
    <div className="fk-preview-popup">
      <div className="fk-preview-title">{fk.label}</div>
      {fields.map(f => {
        const val = getNestedValue(preview, f);
        if (val === undefined || val === null) return null;
        const display = typeof val === 'string' && val.length > 80 ? val.slice(0, 80) + '...' : String(val);
        return (
          <div key={f} className="fk-preview-row">
            <span className="fk-preview-field">{f}:</span> <span className="fk-preview-value">{display}</span>
          </div>
        );
      })}
    </div>
  );
}

function FkLink({ fk, id, onNavigate, children }: {
  fk: FkTarget;
  id: string;
  onNavigate: (container: string, id: string, label: string) => void;
  children: React.ReactNode;
}) {
  const [hover, setHover] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => { timeoutRef.current = setTimeout(() => setHover(true), 300); };
  const handleLeave = () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); setHover(false); };

  return (
    <span className="fk-link-wrap" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <span
        className="explorer-fk-link"
        onClick={() => onNavigate(fk.container, id, `${fk.label}: ${id.slice(0, 12)}...`)}
        title={`Go to ${fk.label} in ${fk.container}`}
      >
        {children}
      </span>
      <span className="explorer-fk-badge">{fk.container}</span>
      {hover && <FkHoverPreview container={fk.container} id={id} fk={fk} />}
    </span>
  );
}

// ─── JSON Renderer ──────────────────────────────────────────────────────────
export function JsonNode({
  keyName,
  value,
  container,
  fieldPath,
  onNavigate,
}: {
  keyName?: string;
  value: unknown;
  container: string;
  fieldPath: string;
  onNavigate: (container: string, id: string, label: string) => void;
}) {
  const containerFks = FK_MAP[container] || {};
  const containerArrayFks = ARRAY_FK_MAP[container] || {};

  if (value === null) {
    return <span className="json-null">null</span>;
  }
  if (typeof value === 'boolean') {
    return <span className="json-boolean">{value ? 'true' : 'false'}</span>;
  }
  if (typeof value === 'number') {
    return <span className="json-number">{value}</span>;
  }
  if (typeof value === 'string') {
    // Normalize path: strip array indices so "questionResults[0].questionId" → "questionResults.questionId"
    const normalizedPath = fieldPath.replace(/\[\d+\]/g, '');
    const fk = containerFks[fieldPath] || containerFks[normalizedPath];
    if (fk && value.length > 0) {
      return (
        <FkLink fk={fk} id={value} onNavigate={onNavigate}>
          &quot;{value}&quot;
        </FkLink>
      );
    }
    return <span className="json-string">&quot;{value}&quot;</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="json-bracket">[]</span>;

    const arrayFk = containerArrayFks[keyName || ''];

    return (
      <span>
        <span className="json-bracket">[</span>
        <div className="json-indent">
          {value.map((item, i) => (
            <div key={i}>
              {arrayFk && typeof item === 'string' && item.length > 0 ? (
                <FkLink fk={arrayFk} id={item} onNavigate={onNavigate}>
                  &quot;{item}&quot;
                </FkLink>
              ) : (
                <JsonNode
                  value={item}
                  container={container}
                  fieldPath={`${fieldPath}[${i}]`}
                  onNavigate={onNavigate}
                />
              )}
              {i < value.length - 1 && <span className="json-comma">,</span>}
            </div>
          ))}
        </div>
        <span className="json-bracket">]</span>
      </span>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return <span className="json-bracket">{'{}'}</span>;

    return (
      <span>
        <span className="json-bracket">{'{'}</span>
        <div className="json-indent">
          {entries.map(([k, v], i) => {
            const childPath = fieldPath ? `${fieldPath}.${k}` : k;
            return (
              <div key={k}>
                <span className="json-key">&quot;{k}&quot;</span>
                <span className="json-colon">: </span>
                <JsonNode
                  keyName={k}
                  value={v}
                  container={container}
                  fieldPath={childPath}
                  onNavigate={onNavigate}
                />
                {i < entries.length - 1 && <span className="json-comma">,</span>}
              </div>
            );
          })}
        </div>
        <span className="json-bracket">{'}'}</span>
      </span>
    );
  }

  return <span>{String(value)}</span>;
}

// ─── Single Explorer Panel ──────────────────────────────────────────────────

function ExplorerPanel({ onNavigateNewTab, initialContainer, initialDocId }: {
  onNavigateNewTab?: (container: string, id: string) => void;
  initialContainer?: string;
  initialDocId?: string;
}) {
  const [container, setContainer] = useState(initialContainer || 'submissions');
  const [docId, setDocId] = useState(initialDocId || '');
  const [treeRoots, setTreeRoots] = useState<TreeNode[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [document, setDocument] = useState<Record<string, unknown> | null>(null);
  const [currentContainer, setCurrentContainer] = useState('');
  const [currentId, setCurrentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detail pane state (shown on wide screens when clicking an FK link)
  const [detailDoc, setDetailDoc] = useState<Record<string, unknown> | null>(null);
  const [detailContainer, setDetailContainer] = useState('');
  const [detailId, setDetailId] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Auto-fetch if opened with initial container/id
  const [autoFetched, setAutoFetched] = useState(false);
  useEffect(() => {
    if (autoFetched || !initialContainer || !initialDocId) return;
    setAutoFetched(true);
    const root = createRootNode(initialContainer, initialDocId);
    setTreeRoots([root]);
    selectAndFetch(initialContainer, initialDocId, root.id);
  }, [initialContainer, initialDocId, autoFetched]);

  const selectAndFetch = useCallback(async (targetContainer: string, targetId: string, nodeId: string) => {
    setSelectedNodeId(nodeId);
    setCurrentContainer(targetContainer);
    setCurrentId(targetId);
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getExplorerDocument(targetContainer, targetId);
      if (!result.document) {
        setError(`No document found with id "${targetId}" in ${targetContainer}`);
        setDocument(null);
      } else {
        setDocument(result.document);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch document');
      setDocument(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleManualFetch = useCallback(() => {
    const trimmedId = docId.trim();
    if (!trimmedId) return;
    const root = createRootNode(container, trimmedId);
    setTreeRoots(prev => [...prev, root]);
    selectAndFetch(container, trimmedId, root.id);
  }, [container, docId, selectAndFetch]);

  const handleTreeSelect = useCallback((targetContainer: string, targetId: string) => {
    const nodeId = `${targetContainer}:${targetId}`;
    selectAndFetch(targetContainer, targetId, nodeId);
  }, [selectAndFetch]);

  const loadDetail = useCallback(async (targetContainer: string, targetId: string) => {
    setDetailContainer(targetContainer);
    setDetailId(targetId);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const result = await adminApi.getExplorerDocument(targetContainer, targetId);
      if (!result.document) {
        setDetailError(`No document found with id "${targetId}" in ${targetContainer}`);
        setDetailDoc(null);
      } else {
        setDetailDoc(result.document);
      }
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : 'Failed to fetch');
      setDetailDoc(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const promoteDetailToMain = useCallback(() => {
    if (!detailContainer || !detailId) return;
    const nodeId = `${detailContainer}:${detailId}`;
    const existsInTree = treeRoots.some(r => findInTree(r, nodeId));
    if (!existsInTree) {
      const newRoot = createRootNode(detailContainer, detailId);
      setTreeRoots(prev => [...prev, newRoot]);
    }
    selectAndFetch(detailContainer, detailId, nodeId);
    setDetailDoc(null);
    setDetailContainer('');
    setDetailId('');
  }, [detailContainer, detailId, treeRoots, selectAndFetch]);

  const handleNavigate = useCallback((targetContainer: string, targetId: string, _label: string) => {
    // On wide screens, show in detail pane; on narrow, navigate in main pane
    if (window.innerWidth >= 1200) {
      loadDetail(targetContainer, targetId);
    } else {
      const nodeId = `${targetContainer}:${targetId}`;
      const existsInTree = treeRoots.some(r => findInTree(r, nodeId));
      if (!existsInTree) {
        const newRoot = createRootNode(targetContainer, targetId);
        setTreeRoots(prev => [...prev, newRoot]);
      }
      selectAndFetch(targetContainer, targetId, nodeId);
    }
  }, [treeRoots, selectAndFetch, loadDetail]);

  const handleDetailNavigate = useCallback((targetContainer: string, targetId: string, _label: string) => {
    loadDetail(targetContainer, targetId);
  }, [loadDetail]);

  const hasDetail = !!(detailDoc || detailLoading || detailError);

  return (
    <div className={`explorer-layout${hasDetail ? ' has-detail' : ''}`}>
      {/* Left Panel */}
      <div className="explorer-sidebar">
        <div className="explorer-form">
          <label className="explorer-label">Container</label>
          <select
            value={container}
            onChange={(e) => setContainer(e.target.value)}
            className="explorer-select"
          >
            {CONTAINERS.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <label className="explorer-label">Document ID</label>
          <input
            type="text"
            value={docId}
            onChange={(e) => setDocId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleManualFetch()}
            placeholder="Paste document ID..."
            className="explorer-input"
          />

          <button
            onClick={handleManualFetch}
            disabled={loading || !docId.trim()}
            className="explorer-fetch-btn"
          >
            {loading ? 'Fetching...' : 'Fetch'}
          </button>
        </div>

        {treeRoots.length > 0 && (
          <div className="explorer-tree-section">
            <label className="explorer-label">Document Tree</label>
            <div className="explorer-tree-hint">Double-click to expand references</div>
            <ExplorerTree
              roots={treeRoots}
              selectedNodeId={selectedNodeId}
              onSelect={handleTreeSelect}
              onUpdateRoots={setTreeRoots}
            />
          </div>
        )}
      </div>

      {/* Right Panel */}
      <div className="explorer-content">
        {!document && !loading && !error && (
          <div className="explorer-placeholder">
            Select a container and enter a document ID to view its contents.
            <br />
            FK fields will be highlighted — click them to navigate to related documents.
          </div>
        )}

        {loading && (
          <div className="explorer-placeholder">Loading document...</div>
        )}

        {error && (
          <div className="explorer-error">{error}</div>
        )}

        {document && !loading && (
          <div className="explorer-document">
            <div className="explorer-doc-header">
              <span className="explorer-container-badge">{currentContainer}</span>
              <span className="explorer-doc-id">{currentId}</span>
              {onNavigateNewTab && (
                <button
                  className="explorer-open-tab-btn"
                  onClick={() => onNavigateNewTab(currentContainer, currentId)}
                  title="Open in new tab"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </button>
              )}
            </div>
            <pre className="explorer-json">
              <JsonNode
                value={document}
                container={currentContainer}
                fieldPath=""
                onNavigate={handleNavigate}
              />
            </pre>
          </div>
        )}
      </div>

      {/* Detail Pane (visible on wide screens when an FK link is clicked) */}
      <div className="explorer-detail-pane">
        {detailLoading && (
          <div className="detail-placeholder">Loading...</div>
        )}
        {detailError && (
          <div className="explorer-error">{detailError}</div>
        )}
        {detailDoc && !detailLoading && (
          <div className="explorer-document">
            <div className="explorer-doc-header">
              <span className="explorer-container-badge">{detailContainer}</span>
              <span className="explorer-doc-id">{detailId}</span>
              <button
                className="explorer-open-tab-btn"
                onClick={promoteDetailToMain}
                title="Open in main pane"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              {onNavigateNewTab && (
                <button
                  className="explorer-open-tab-btn"
                  onClick={() => onNavigateNewTab(detailContainer, detailId)}
                  title="Open in new tab"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </button>
              )}
            </div>
            <pre className="explorer-json">
              <JsonNode
                value={detailDoc}
                container={detailContainer}
                fieldPath=""
                onNavigate={handleDetailNavigate}
              />
            </pre>
          </div>
        )}
        {!detailDoc && !detailLoading && !detailError && (
          <div className="detail-placeholder">
            Click an FK link in the document to preview it here.
          </div>
        )}
      </div>
    </div>
  );
}

function findInTree(node: TreeNode, nodeId: string): boolean {
  if (node.id === nodeId) return true;
  return node.children?.some(c => findInTree(c, nodeId)) ?? false;
}

// ─── Tabbed Explorer ────────────────────────────────────────────────────────
interface TabInfo {
  id: number;
  label: string;
  initialContainer?: string;
  initialDocId?: string;
}

let nextTabId = 2;

interface ExplorerTabProps {
  initialContainer?: string;
  initialDocId?: string;
}

export function ExplorerTab({ initialContainer, initialDocId }: ExplorerTabProps = {}) {
  const [tabs, setTabs] = useState<TabInfo[]>([
    initialContainer && initialDocId
      ? { id: 1, label: `${initialContainer}: ${initialDocId.slice(0, 10)}...`, initialContainer, initialDocId }
      : { id: 1, label: 'Tab 1' },
  ]);
  const [activeTabId, setActiveTabId] = useState(1);

  // If the URL changes (user clicks another IdControl → "Open in Explorer" while
  // already on Settings), open the new target in a fresh tab.
  useEffect(() => {
    if (!initialContainer || !initialDocId) return;
    const exists = tabs.some(t => t.initialContainer === initialContainer && t.initialDocId === initialDocId);
    if (exists) {
      const match = tabs.find(t => t.initialContainer === initialContainer && t.initialDocId === initialDocId)!;
      setActiveTabId(match.id);
      return;
    }
    const id = nextTabId++;
    setTabs(prev => [...prev, { id, label: `${initialContainer}: ${initialDocId.slice(0, 10)}...`, initialContainer, initialDocId }]);
    setActiveTabId(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialContainer, initialDocId]);

  const addTab = useCallback((label?: string, initialContainer?: string, initialDocId?: string) => {
    const id = nextTabId++;
    setTabs(prev => [...prev, { id, label: label || `Tab ${id}`, initialContainer, initialDocId }]);
    setActiveTabId(id);
    return id;
  }, []);

  const closeTab = useCallback((tabId: number) => {
    setTabs(prev => {
      const filtered = prev.filter(t => t.id !== tabId);
      if (filtered.length === 0) {
        // Always keep at least one tab
        const newTab = { id: nextTabId++, label: 'Tab 1' };
        return [newTab];
      }
      return filtered;
    });
    setActiveTabId(prev => {
      if (prev !== tabId) return prev;
      const remaining = tabs.filter(t => t.id !== tabId);
      if (remaining.length === 0) return nextTabId - 1;
      // Activate the tab to the left, or the first remaining
      const closedIndex = tabs.findIndex(t => t.id === tabId);
      const newIndex = Math.min(closedIndex, remaining.length - 1);
      return remaining[newIndex].id;
    });
  }, [tabs]);

  const handleNavigateNewTab = useCallback((container: string, id: string) => {
    addTab(`${container}: ${id.slice(0, 10)}...`, container, id);
  }, [addTab]);

  return (
    <div className="explorer-tabs-container">
      <div className="explorer-tabs-bar">
        {tabs.map(tab => (
          <div
            key={tab.id}
            className={`explorer-tab ${tab.id === activeTabId ? 'active' : ''}`}
          >
            <button
              className="explorer-tab-label"
              onClick={() => setActiveTabId(tab.id)}
            >
              {tab.label}
            </button>
            {tabs.length > 1 && (
              <button
                className="explorer-tab-close"
                onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
                title="Close tab"
              >
                ×
              </button>
            )}
          </div>
        ))}
        <button
          className="explorer-tab-add"
          onClick={() => addTab()}
          title="New tab"
        >
          +
        </button>
      </div>
      <div className="explorer-tabs-content">
        {tabs.map(tab => (
          <div
            key={tab.id}
            className="explorer-tab-panel"
            style={{ display: tab.id === activeTabId ? 'block' : 'none' }}
          >
            <ExplorerPanel
              onNavigateNewTab={handleNavigateNewTab}
              initialContainer={tab.initialContainer}
              initialDocId={tab.initialDocId}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
